/**
 * @fileoverview Circuit Breaker Pattern Implementation
 * @module services/tracking/circuit-breaker
 *
 * Implements the circuit breaker pattern to prevent cascading failures
 * when the tracking API is unavailable.
 */

/**
 * Circuit breaker states
 */
export type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN'

/**
 * Configuration for circuit breaker
 */
export interface CircuitBreakerConfig {
  /** Number of failures before opening circuit (default: 3) */
  failureThreshold: number
  /** Time in milliseconds before attempting to recover (default: 60000) */
  timeout: number
  /** Enable automatic state transitions (default: true) */
  autoTransition: boolean
}

/**
 * Circuit breaker metrics
 */
export interface CircuitBreakerMetrics {
  /** Current state */
  state: CircuitBreakerState
  /** Number of consecutive failures */
  failureCount: number
  /** Timestamp of last failure */
  lastFailureTime: number | null
  /** Timestamp when circuit will attempt to recover */
  recoveryTime: number | null
  /** Total number of requests blocked */
  blockedRequests: number
}

/**
 * Circuit Breaker
 *
 * Implements the circuit breaker pattern to prevent cascading failures.
 * Stops sending requests when backend is consistently failing.
 *
 * States:
 * - CLOSED: Normal operation, requests are sent
 * - OPEN: Backend is failing, requests are blocked
 * - HALF_OPEN: Testing if backend has recovered, one request allowed
 *
 * @example
 * ```typescript
 * const breaker = new CircuitBreaker({
 *   failureThreshold: 3,
 *   timeout: 60000,
 *   autoTransition: true
 * });
 *
 * // Check if request should be sent
 * if (breaker.canAttempt()) {
 *   try {
 *     await sendRequest();
 *     breaker.recordSuccess();
 *   } catch (error) {
 *     breaker.recordFailure();
 *   }
 * }
 * ```
 */
export class CircuitBreaker {
  private readonly config: CircuitBreakerConfig
  private state: CircuitBreakerState = 'CLOSED'
  private failureCount = 0
  private lastFailureTime: number | null = null
  private recoveryTime: number | null = null
  private blockedRequests = 0

  /**
   * Create a new circuit breaker instance
   * @param config - Configuration options
   */
  constructor(config: Partial<CircuitBreakerConfig> = {}) {
    this.config = {
      failureThreshold: config.failureThreshold ?? 3,
      timeout: config.timeout ?? 60000,
      autoTransition: config.autoTransition ?? true,
    }

    // Validate configuration
    if (this.config.failureThreshold <= 0) {
      throw new Error('failureThreshold must be greater than 0')
    }
    if (this.config.timeout <= 0) {
      throw new Error('timeout must be greater than 0')
    }
  }

  /**
   * Check if a request can be attempted
   * @returns true if request should be sent, false if blocked
   */
  canAttempt(): boolean {
    if (this.state === 'CLOSED') {
      return true
    }

    if (this.state === 'OPEN') {
      // Check if timeout has passed
      if (this.config.autoTransition && this.recoveryTime && Date.now() >= this.recoveryTime) {
        this.state = 'HALF_OPEN'
        return true
      }
      return false
    }

    // HALF_OPEN - allow one request to test
    return true
  }

  /**
   * Record a successful request
   * Resets the circuit breaker to CLOSED state
   */
  recordSuccess(): void {
    this.failureCount = 0
    this.lastFailureTime = null
    this.recoveryTime = null

    if (this.state !== 'CLOSED') {
      this.state = 'CLOSED'
    }
  }

  /**
   * Record a failed request
   * May transition to OPEN state if threshold is exceeded
   */
  recordFailure(): void {
    this.failureCount++
    this.lastFailureTime = Date.now()

    if (this.state === 'HALF_OPEN') {
      // Failed during recovery attempt, go back to OPEN
      this.state = 'OPEN'
      this.recoveryTime = Date.now() + this.config.timeout
    } else if (this.failureCount >= this.config.failureThreshold) {
      // Threshold exceeded, open the circuit
      this.state = 'OPEN'
      this.recoveryTime = Date.now() + this.config.timeout
    }
  }

  /**
   * Get the current state
   * @returns Current circuit breaker state
   */
  getState(): CircuitBreakerState {
    // Auto-transition from OPEN to HALF_OPEN if timeout passed
    if (this.config.autoTransition && this.state === 'OPEN' && this.recoveryTime && Date.now() >= this.recoveryTime) {
      this.state = 'HALF_OPEN'
    }

    return this.state
  }

  /**
   * Manually set the state (for testing)
   * @param newState - New state to set
   */
  setState(newState: CircuitBreakerState): void {
    this.state = newState

    if (newState === 'CLOSED') {
      this.failureCount = 0
      this.lastFailureTime = null
      this.recoveryTime = null
    } else if (newState === 'OPEN') {
      this.recoveryTime = Date.now() + this.config.timeout
    } else if (newState === 'HALF_OPEN') {
      this.recoveryTime = null
    }
  }

  /**
   * Get current metrics
   * @returns Circuit breaker metrics
   */
  getMetrics(): CircuitBreakerMetrics {
    return {
      state: this.getState(),
      failureCount: this.failureCount,
      lastFailureTime: this.lastFailureTime,
      recoveryTime: this.recoveryTime,
      blockedRequests: this.blockedRequests,
    }
  }

  /**
   * Record a blocked request (for metrics)
   */
  recordBlockedRequest(): void {
    this.blockedRequests++
  }

  /**
   * Reset the circuit breaker to initial state
   */
  reset(): void {
    this.state = 'CLOSED'
    this.failureCount = 0
    this.lastFailureTime = null
    this.recoveryTime = null
    this.blockedRequests = 0
  }

  /**
   * Get time until circuit attempts recovery (in milliseconds)
   * @returns Milliseconds until recovery attempt, or -1 if not applicable
   */
  getTimeUntilRecovery(): number {
    if (this.state !== 'OPEN' || !this.recoveryTime) {
      return -1
    }

    const timeUntil = this.recoveryTime - Date.now()
    return Math.max(0, timeUntil)
  }

  /**
   * Check if circuit is currently open
   * @returns true if circuit is open
   */
  isOpen(): boolean {
    return this.getState() === 'OPEN'
  }

  /**
   * Check if circuit is currently closed
   * @returns true if circuit is closed
   */
  isClosed(): boolean {
    return this.getState() === 'CLOSED'
  }

  /**
   * Check if circuit is in half-open state
   * @returns true if circuit is half-open
   */
  isHalfOpen(): boolean {
    return this.getState() === 'HALF_OPEN'
  }

  /**
   * Get configuration
   * @returns Current configuration
   */
  getConfig(): Readonly<CircuitBreakerConfig> {
    return Object.freeze({ ...this.config })
  }
}

